import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router'
import { toast } from 'react-toastify'
import ReleaseStatus from '../../../../constants/ReleaseStatus'
import ClientTypesConstants from '../../../../constants/ClientTypes'
import MandatoryScopes from '../../../../constants/MandatoryScopes'
import useFormState from '../../../../hooks/useFormState'
import { getProductPropertyFormats } from '../../../../redux/actions/lookupData'
import { createRelease, getReleaseById } from '../../../../redux/actions/release'
import LoadingBlock from '../../../common/LoadingBlock'
import SectionHeader from '../../../common/SectionHeader'
import {
  ApplicationAccessForm,
  ApplicationPropertiesForm,
  BasicSettingsForm,
  ClientsForm,
  EnvironmentInfoForm,
  VersionInfoForm
} from '../../../common/SharedForms'
import basicSettingsValidationConfig from '../../../common/SharedForms/BasicSettingsForm/validationConfig'

const ApplicationDetail = () => {
  // ===== Hooks =====
  const { releaseId } = useParams()
  const history = useHistory()
  // Form states
  const versionInfoFormState = useFormState()
  const environmentInfoFormState = useFormState()
  const basicSettingsFormState = useFormState()
  const appAccessFormState = useFormState()
  const appPropertiesFormState = useFormState()
  const webClientsFormState = useFormState()
  const serverClientsFormState = useFormState()
  const spaClientsFormState = useFormState()
  const mobileClientsFormState = useFormState()

  const { setFormData: setFormDataVersionInfo } = versionInfoFormState
  const { setFormData: setFormDataEnvironmentInfo } = environmentInfoFormState
  const {
    formData: formDataBasicSettings,
    setFormData: setFormDataBasicSettings,
    validateForm: validateFormBasicSettings,
    setFormIsDirty: setFormIsDirtyBasicSettings
  } = basicSettingsFormState
  const { setFormData: setFormDataAppAccess } = appAccessFormState
  const { setFormData: setFormDataAppProperties } = appPropertiesFormState
  const { setFormData: setFormDataWebClient } = webClientsFormState
  const { setFormData: setFormDataServerClient } = serverClientsFormState
  const { setFormData: setFormDataSpaClient } = spaClientsFormState
  const { setFormData: setFormDataMobileClient } = mobileClientsFormState

  // ===== Constants =====
  const IS_CREATE_NEW_MODE = releaseId.toString() === "0"

  // ===== Redux state =====
  const dispatch = useDispatch()
  const release = useSelector(({ release }) => release.release)
  const isFetchingCurrentRelease = useSelector(({ release }) => release.isFetchingCurrentRelease)

  // ===== Side effects =====
  useEffect(() => {
    if (releaseId && !IS_CREATE_NEW_MODE) {
      dispatch(getReleaseById(releaseId))
    }
  }, [releaseId])

  useEffect(() => {
    // Initializing form data
    if (!IS_CREATE_NEW_MODE && (!isFetchingCurrentRelease) && release?.id?.toString() === releaseId) {
      initializeFormData()
    }
  }, [isFetchingCurrentRelease, release?.updatedDate])

  useEffect(() => {
    // Initializing application property formats
    const app = release?.data ? JSON.parse(release.data) : null
    const categoryName = app?.applicationCategory?.name

    if (categoryName) {
      dispatch(getProductPropertyFormats(categoryName))
    }
  }, [release?.data])

  // ===== Methods =====
  const initializeFormData = () => {
    // Parsing release.data to get the application object
    const app = getApplication()
    // Prevent null pointers
    if (!app) return

    // VersionInfo
    setFormDataVersionInfo({
      releaseId: release.id,
      commentByVendor: release.commentByVendor?.commentText,
      commentByAdmin: release.commentByAdmin?.commentText
    })

    // EnvironmentInfo
    setFormDataEnvironmentInfo({
      requestPlatformCodes: release.requestPlatformCodes
        ?.split(',')
        ?.map(x => ({ value: x })) || []
    })

    // BasicSettings
    setFormDataBasicSettings({
      name: app.name,
      description: app.description,
      applicationCategoryId: app.applicationCategoryId,
      isSingleTenant: app.isSingleTenant,
      image: app.logoImage?.path ? `${process.env.REACT_APP_DEV_PORTAL_API_BASE_URL}/${app.logoImage?.path}` : null,
      agencyCompanyKey: app.agencyCompanyKey
    })
    // AppAccess
    setFormDataAppAccess({
      applicationScopes: app.applicationScopes?.filter(s => s.value !== MandatoryScopes.APPFRAMEWORK)
    })
    // AppProperties
    const appPropertiesObj = {}
    app.applicationProperties?.forEach(p => appPropertiesObj[p.key] = p.value)
    setFormDataAppProperties(appPropertiesObj)

    // Clients
    const webClientObj = app.applicationClients?.find(c => c.clientType === ClientTypesConstants.WEB)
    const serverClientObj = app.applicationClients?.find(c => c.clientType === ClientTypesConstants.SERVER)
    const spaClientObj = app.applicationClients?.find(c => c.clientType === ClientTypesConstants.SPA)
    const mobileClientObj = app.applicationClients?.find(c => c.clientType === ClientTypesConstants.MOBILE)
    setFormDataWebClient(webClientObj)
    setFormDataServerClient(serverClientObj)
    setFormDataSpaClient(spaClientObj)
    setFormDataMobileClient(mobileClientObj)
  }

  const getApplication = () => {
    // Parsing release.data to get the application object
    let app = null
    try {
      app = release?.data ? JSON.parse(release?.data) : null
    } catch (e) {/* Fail Silently */ }

    return app
  }

  const transformFormDataToRelease = () => {
    const release = {
      version: 1,
      application: {
        ...formDataBasicSettings,
        isInternal: true
      }
    }
    return release
  }

  const resetAllFormsDirtiness = () => {
    setFormIsDirtyBasicSettings(false)
  }

  const onPublishClicked = () => {
    history.push(`/admin/all-applications/publish/${releaseId}`)
  }

  const onSaveClicked = async () => {
    const isValidBasicSettings = validateFormBasicSettings(basicSettingsValidationConfig)
    const formIsValid = isValidBasicSettings

    if (!formIsValid) {
      toast.error('Please fix all validation issues!')
      return false
    }

    const app = transformFormDataToRelease()
    const res = await dispatch(createRelease(app))

    if (res) {
      toast.success('Application successfully updated!')
      resetAllFormsDirtiness()
      history.push(`/admin/all-applications/${res?.id}`)
      return true
    } else {
      toast.error('Something went wrong, Try again!')
      return false
    }
  }

  const getTitleText = () => {
    if (IS_CREATE_NEW_MODE) {
      return "v1.0 - New App"
    }
    return `v${release?.version?.toFixed(1)} - ${getApplication()?.name}`
  }

  const getMainButtonText = () => {
    if (IS_CREATE_NEW_MODE) {
      return "Save"
    }
    return "Publish"
  }

  const getMainButtonOnClick = () => {
    if (IS_CREATE_NEW_MODE) {
      return onSaveClicked
    }
    return release?.status === ReleaseStatus.APPROVED || release?.status === ReleaseStatus.LIVE ? onPublishClicked : null
  }

  const getStatusString = (status) => {
    switch (status) {
      case ReleaseStatus.APPROVED:
        return "Not Published"
      case ReleaseStatus.LIVE:
        return "Live"
      case ReleaseStatus.EXPIRED:
        return "Expired"
      default:
        return null
    }
  }

  const isInternalApp = () => {
    const app = getApplication()
    return app?.isInternal
  }

  // ==== Render =====
  if (!IS_CREATE_NEW_MODE && (isFetchingCurrentRelease || !release || release?.id?.toString() !== releaseId)) {
    return <LoadingBlock message="Loading..." />
  }
  return (
    <div className="application-detail-container">
      <SectionHeader
        title={getTitleText()}
        mainButtonText={getMainButtonText()}
        mainButtonOnClick={(getMainButtonOnClick())}
        backButtonText="Applications"
        backButtonOnClick={() => { history.push('/admin/all-applications') }}
        status={getStatusString(release?.status)}
      />
      {!IS_CREATE_NEW_MODE && (
        <VersionInfoForm
          formState={versionInfoFormState}
          globalApplicationId={release?.globalApplicationId}
          isApplicationView={true}
          disabled={true}
          isInternalApp={isInternalApp()}
        />
      )}
      {(!IS_CREATE_NEW_MODE && !isInternalApp()) && (
        <EnvironmentInfoForm
          formState={environmentInfoFormState}
          disabled={true}
        />
      )}
      <BasicSettingsForm
        formState={basicSettingsFormState}
        disabled={!IS_CREATE_NEW_MODE}
        isInternalApp={isInternalApp()}
        isCreateNewMode={IS_CREATE_NEW_MODE}
      />
      {(!IS_CREATE_NEW_MODE && !isInternalApp()) && (
        <>
          <ApplicationAccessForm
            formState={appAccessFormState}
            appId={release?.applicationId}
            disabled={true}
          />
          <ClientsForm
            formStateWeb={webClientsFormState}
            formStateServer={serverClientsFormState}
            formStateSpa={spaClientsFormState}
            formStateMobile={mobileClientsFormState}
            appScopes={appAccessFormState?.formData?.applicationScopes || []}
            releaseId={release?.id}
            releaseStatus={release?.status}
            disabled={true}
          />
          <ApplicationPropertiesForm
            formState={appPropertiesFormState}
            disabled={true}
          />
        </>
      )}
      <div className="spacer" />
    </div>
  )
}

export default ApplicationDetail